import React, { useEffect, useState } from 'react';
import useAuthService from 'hooks/useAuthService';
import { useNavigate, useParams } from 'react-router-dom';
import withAuthorization from 'components/hocs/withAuthorization';
import { RealmRoles } from '../../constants';
import useGetClaimDetail from './hooks/get-claim-detail.hook';
import { Button, Card, Col, Divider, List, Row, Skeleton, Space, Table, Typography, Tag } from 'antd';
import Page from 'components/page';
import ClaimStatusTag from './components/claim-status-tag';
import { DataDisplayContainer } from 'components/styles';
import DataDisplay from 'components/data-display';
import { ColumnsType } from 'antd/lib/table';
import { IClaimTransaction } from './interfaces/IClaimTransaction';
import { getProperDate, sortDateDesc } from 'utils/date';
import { TransactionHelper } from 'pages/transactions/helper/transactions.helper';
import { EyeOutlined, ToolOutlined } from '@ant-design/icons';
import ModalTransactionsAdjustment from './components/transaction-adjustment-modal';
import { AdjustmentModalDTO } from './dto/adjustment-modal.dto';
import PrepaidComment from 'components/comment/prepaid-comment';
import PrepaidCommentCreator from 'components/comment/prepaid-comment-creator';
import { PrepaidCommentDTO } from 'components/comment/dto/prepaid-comment.dto';
import { IClaimComment } from './interfaces/IClaimComment';
import { CustomComment } from 'components/comment/custom-comment';
import useGetClaimComments from './hooks/get-claim-comments.hook';
import { ClaimStatus, ClaimStatusLabels } from './enums/claim-status.enum';
import StateModal from 'components/modal/state/change-state.modal';
import usePostRequestUpgrade from 'hooks/utils/usePostRequestUpgrade';
import { StateModalDTO } from 'components/modal/state/state-modal.dto';
import { CreateClaimStateDTO } from './dto/create-claim-state.dto';
import useToastContext from 'hooks/useToastContext';
import getFileIcon from './helper/file.helper';
import useGetRequestUpgrade from '../../hooks/utils/useGetRequestUpgrade';
import config from 'config';
import { validateStatus } from '../../utils/utils';
import { TransactionAdjustment } from './interfaces/ITransactionAdjustment';
import { TransactionAdjustmentType } from './enums/adjustment-type.enum';
import { AdjustmentStatus } from './enums/adjustment-status.enum';
const url: string = config.api.url;

const { Title } = Typography;

const ClaimDetail = () => {
    const { id, issuer} = useParams<{ id: string, issuer: string }>();
    const navigate = useNavigate();
    const { userInfo, userHasRole } = useAuthService();
    const [stateModalOpen, setStateModalOpen] = useState(false);
    const [modalAdjustmentOpen, setModalAdjustmentOpen] = useState(false);
    const [transactionAdjustment, setTransactionAdjustment] = useState<AdjustmentModalDTO>();
    const { claim, isLoading, isError, refetchClaim } = useGetClaimDetail(id);
    const { postLoading, executePost } = usePostRequestUpgrade({ path: `v1/claims/${id}/transitions` });
    const { postLoading: createCommentLoading, executePost: createComment } = usePostRequestUpgrade({ path: `v1/claims/${id}/comments` });
    const toast = useToastContext();
    const [commentsPaginationModel, setCommentsPaginationModel] = useState({
      page: 0,
      pageSize: 10,
    });
    const { comments, pageInfo: commentPageInfor, isLoading:commentLoading, isError: commentError, executeGetClaimComments } = useGetClaimComments(
      id,
      commentsPaginationModel.page,
      commentsPaginationModel.pageSize,
    );
    const { executeGet: executeGetFile } = useGetRequestUpgrade({ path : `v2/exporter/files?process=CLAIMS_TYPE`, manual: true });
    const { executeGet: executeGetAdjustment } = useGetRequestUpgrade({ path : `v1/adjustments?page=0&size=100`, manual: true });
    const [adjustments, setAdjustments] = useState([]);

    const onClickViewTransactionDetails = (trxId: string, cardHolderId: string, date: string, transactionType: string) => {
      var cardId = claim?.card?.id?.replace(/\s+/g, "");
      var txnDate = date?.split(".")[0]?.replace(" ", "T");
      navigate(`/${issuer}/transactions/${cardId}/${trxId}?cardHolderId=${cardHolderId}&date=${txnDate}&transactionType=${transactionType}`);
    };

    const AdjustmentButton = withAuthorization([RealmRoles.TransactionAdjustmentWrite], ({ txn }: { txn: IClaimTransaction }) => (
      <Button
        size="small"
        type="link"
        onClick={() => {
          getTxnAdjustment(txn);
          setModalAdjustmentOpen(true);
        } }
        icon={ txn.adjustment != undefined ? <EyeOutlined /> : <ToolOutlined /> }
      >
      {adjustments?.find( adj => adj.transaction_id === txn?.rrn) ? "Ver Ajuste" : "Ajustar"}
      </Button>
    ));

    const mapTransactionAdjustment = (adjustment) => {
      if(adjustments?.length < 1 || adjustment === null || adjustment === undefined) return undefined;

      return new TransactionAdjustment(
        adjustment.transaction_id,
        adjustment.amount,
        TransactionAdjustmentType[adjustment.type],
        adjustment.reason,
        adjustment.dispute_id,
        adjustment.chargeback_id,
        adjustment.brand_claim_id,
        AdjustmentStatus[adjustment.status]
      )
    }

    const getTxnAdjustment = (txn: IClaimTransaction) => {
      setTransactionAdjustment(new AdjustmentModalDTO(
        txn.rrn,
        claim.card.id,
        claim.cardholder.id,
        txn.date,
        txn.amount,
        mapTransactionAdjustment(adjustments.find(adj => adj.transaction_id === txn.rrn))
      ));
    }

    const columns: ColumnsType<IClaimTransaction> = [
      {
        dataIndex: 'rrn',
        key: 'rrn',
        title: 'Ticket',
      },
      {
        dataIndex: 'date',
        key: 'date',
        title: 'Fecha',
        defaultSortOrder: 'descend',
        sorter: (a, b) => sortDateDesc(b.date, a.date),
        render: (_, record) => {
          return getProperDate(record.date);
        },
      },
      {
        dataIndex: 'transactionType',
        key: 'transactionType',
        title: 'Tipo',
        render: (_, record) => {
          // @ts-ignore
          return TransactionHelper.getTransactionType(record.transaction_type);
        },
      },
      {
           dataIndex: 'currency', 
           key: 'currency', 
           title: 'Moneda' },
      {
           dataIndex: 'amount', 
           key: 'amount',
           title: 'Monto',
           render: (_, record) => {
              return TransactionHelper.getTransactionAmountFormatted(record.currency, record.amount);
            },
      },
      {
        dataIndex: 'actions',
        key: 'actions',
        title: 'Acciones',
        render: (_, record) => {
          return (
            <Space>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  onClickViewTransactionDetails(
                      record?.rrn,
                      claim?.cardholder?.id,
                      record?.date,
                      // @ts-ignore
                      record?.transaction_type
                    );
                }}
                size="small"
                type="link"
                icon={<EyeOutlined />}
              >
                Ver Transaccion
              </Button>
              <AdjustmentButton txn={record} />
            </Space>
          );
        },
      },
    ];

    const handleCommentPaginationChange = (page: number, pageSize: number) => {
      setCommentsPaginationModel({ page: page - 1, pageSize });
    };

    const mapClaimCommentsToCustomComments = (comments: IClaimComment[]): CustomComment[] => {
      return comments?.map(
        (c) =>
          new CustomComment(
            String(c.id),
            c.username,
            c.created_date,
            c.comment,
            c.last_update,
            c.attachments_file_names
          )
      );
    };

  const handleCreateComment = async (comment: PrepaidCommentDTO) => {
    try {
      const response = await createComment({ data: {
          comment: comment?.text,
          username: userInfo.name as string,
          attachments_file_names: comment?.attachments
        }
      });

      if (response?.status == 201) {
        toast.success('Éxito', `Comentario creado correctamente!.`);
        // @ts-ignore
        comments.push(response?.data as IClaimComment)
      } else {
        throw new Error();
      }
    } catch (e: unknown) {
      toast.error('Error', `Ocurrió un error al crear el comentario, porfavor, intentá nuevamente.`);
    }
  }

  const getStates = (): Record<string, string> => {
    return (claim?.username === userInfo?.name)
      ? { [ClaimStatusLabels[ClaimStatus.CLOSED_BY_ISSUER]]: ClaimStatus.CLOSED_BY_ISSUER } // 🔄 Swapped key and value
      : Object.fromEntries(Object.values(ClaimStatus).map((status) => [ClaimStatusLabels[status], status])); // 🔄 Swap key-value pairs
  };

  const onStateModalCancel = () => {
    setStateModalOpen(false)
  }

  const onStateModalSubmit = async (state: StateModalDTO) =>{
    try {
      const response = await executePost({ data: new CreateClaimStateDTO(state.state as ClaimStatus, state.observation)});
      if (response?.status == 204) {
        toast.success('Éxito', `Se modificó el estado correctamente.`);
        claim ? (claim.status = ClaimStatus[state?.state]) : null;
        setStateModalOpen(false)
      } else {
        throw new Error();
      }
    } catch (e: unknown) {
      toast.error('Error', `Ocurrió un error al modificar el estado del reclamo, porfavor intentá nuevamente.`);
    }
  }

  const handleGetFile = async (file: string) => {
    const response = await executeGetFile( { url: url + `/v2/exporter/files?name=${file}&process=CLAIMS_LOAD` });

    if(response?.status === 200 || response?.status === 204) {
      toast.success('Exito', 'Descarga satisfactoria!.');
      // @ts-ignore
      window.open(response?.data?.url);
    }else {
      toast.error('Error', 'Ocurrió un problema al descargar, porfavor, intenta nuevamente.')
    }
  };

  const validateField = (value: string) => {
    return value !== null && value !== '' && value !== undefined;
  }

  useEffect(() => {
    if (!claim?.transactions) return;

    let isMounted = true;

    const fetchAdjustments = async () => {
      try {
        const results = [];
        for (const transaction of claim.transactions) {
          const urlFinal = url + `/v1/adjustments?page=0&size=10` +
            (validateField(transaction?.rrn) ? `&transaction_id=${transaction?.rrn}` : '');

          const response = await executeGetAdjustment({ url: urlFinal });

          if (validateStatus(response?.status)) {
            // @ts-ignore
            results.push(...(response?.data?.data || []));
          }
        }
        setAdjustments(results);

      } catch (error) {
        if (isMounted) {
          console.error('Error al obtener ajustes:', error);
        }
      }
    };

    fetchAdjustments();
  }, [claim?.transactions]);




  return (
        <Page
          transparent={true}
          title={ isLoading ? `Reclamo:` : `Reclamo: ${claim?.id}`}
        >
            {isLoading ? (
                <Skeleton />
             ) : (
                <>
                  <Card
                    title={
                      <Row gutter={24} style={{ paddingTop: '20px' }}>
                        <Col span={12}>
                          <Title level={4}>Detalles</Title>
                        </Col>
                        <Col span={12} style={{ textAlign: 'end' }}>
                          <Button type="primary" shape="round" onClick={() => setStateModalOpen(true)} style={{ marginRight: '10px' }}>
                            Cambiar Estado
                          </Button>
                        </Col>
                      </Row>
                    }
                    loading={isLoading}
                    variant="borderless"
                    style={{
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease",
                      width: '100%'
                    }}
                  >
                    <Row gutter={24}>
                      <Col span={6}>
                        <DataDisplay  title="Fecha: " value={claim?.created_date && getProperDate(claim?.created_date)}/>
                      </Col>
                      <Col span={6}>
                        <DataDisplay  title="Motivo: " value={claim?.dispute_reason}/>
                      </Col>
                      <Col span={6}>
                        <DataDisplay  title="Estado: " value={ <ClaimStatusTag status={claim?.status} />}/>
                      </Col>
                      <Col span={6}>
                        <DataDisplay  title="Creador: " value={claim?.username}/>
                      </Col>
                    </Row>
                  </Card>

                  <Card
                    title={<Title level={4}>Titular</Title>}
                    loading={isLoading}
                    variant="borderless"
                    style={{
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease",
                      width: '100%',
                      marginTop: '10px'
                    }}
                  >
                    <Row gutter={24}>
                      <Col span={6}>
                        <DataDisplay  title="Nombre: " value={claim?.cardholder?.first_name}/>
                      </Col>
                      <Col span={6}>
                        <DataDisplay  title="Apellido: " value={claim?.cardholder?.last_name}/>
                      </Col>
                      <Col span={12}>
                        <DataDisplay  title={`${claim?.cardholder?.identifications[0].type}:`} value={claim?.cardholder?.identifications[0].number}/>
                      </Col>
                    </Row>
                  </Card>


                  <Card
                    title={<Title level={4}>Tarjeta</Title>}
                    loading={isLoading}
                    variant="borderless"
                    style={{
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease",
                      width: '100%',
                      marginTop: '10px'
                    }}
                  >
                    <Row gutter={24}>
                      <Col span={6}>
                        <DataDisplay  title="Tipo: " value={claim?.card.type}/>
                      </Col>
                      <Col span={6}>
                        <DataDisplay  title="Últimos cuatro: " value={claim?.card.last_four}/>
                      </Col>
                      <Col span={4}>
                        <DataDisplay  title="Estado: " value={claim?.card.state}/>
                      </Col>
                      <Col span={8}>
                        <DataDisplay  title="Id: " value={claim?.card.id}/>
                      </Col>
                    </Row>
                  </Card>

                  <Card
                    title={<Title level={4}>Transacciones</Title>}
                    loading={isLoading}
                    variant="borderless"
                    style={{
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease",
                      width: '100%',
                      marginTop: '10px'
                    }}
                  >
                    <Table style={{marginTop:'1em'}}
                           dataSource={claim?.transactions || []}
                           scroll={{ x: true }}
                      // @ts-ignore
                           columns={columns?.filter((e) => Object.keys(e).length > 0)}
                           loading={isLoading}
                           pagination={false}
                    />
                  </Card>

                  <Card
                    title={<Title level={4}>Archivos adjuntos</Title>}
                    loading={isLoading}
                    variant="borderless"
                    style={{
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease",
                      width: '100%',
                      marginTop: '10px'
                    }}
                  >
                    {
                      claim?.attachments_file_names === undefined || claim?.attachments_file_names === null || claim?.attachments_file_names?.length < 1 ? (
                        <a key={'example-file'} rel="noopener noreferrer"
                           style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography.Text type="secondary" style={{ fontSize: 16 }}>Sin archivos adjuntos.</Typography.Text>
                          </div>
                        </a>
                    ) : (
                        <div style={{
                          width: '100%',
                          maxWidth: '100%',
                          overflowX: 'auto',
                          whiteSpace: 'nowrap',
                          minHeight: '100px',
                          maxHeight: '100px',
                        }}>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '1em', marginTop: '1em' }}>
                            {claim?.attachments_file_names?.map((file) => (
                              <a key={file} onClick={() => handleGetFile(file)} rel="noopener noreferrer"
                                 style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  {getFileIcon(file)}
                                  <Typography.Text type="secondary" style={{ fontSize: 14, marginTop: "0.2em" }}>{file}</Typography.Text>
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      )
                    }
                  </Card>
                </>
            )}

          <Card
            loading={commentLoading}
            variant="borderless"
            style={{
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              transform: "translateY(-2px)",
              transition: "all 0.3s ease",
              width: '100%',
              marginTop: '10px'
            }}
          >
            <Title level={4}>Comentarios</Title>
            <PrepaidCommentCreator
              user={userInfo.name as string}
              onCreate={handleCreateComment}
              loading={createCommentLoading}
            />
          </Card>

          {commentLoading ? (
            <Skeleton avatar paragraph={{ rows: 4 }} />
          ) : (
            <>
              <List
                itemLayout="vertical"
                size="large"
                pagination={{
                  total: commentPageInfor?.total_rows,
                  showTotal: (total) => commentPageInfor?.total !== undefined ? `Total: ${total}` : ``,
                  pageSize: commentsPaginationModel.pageSize,
                  current: commentsPaginationModel.page + 1,
                  defaultPageSize: 10,
                  defaultCurrent: 1,
                  onChange: handleCommentPaginationChange,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  showSizeChanger: true,
                }}
              dataSource={mapClaimCommentsToCustomComments(comments)}
              renderItem={(item) => (
                <List.Item style={{ paddingLeft: '0px', paddingRight: '0px', borderBottom: 'none' }}>
                    <PrepaidComment claimId = {claim?.id} isOwner={item?.user == userInfo?.name}  comment={item} />
                </List.Item>
              )}
            />
          </>
        )}
          <ModalTransactionsAdjustment
            txnAdjustment={transactionAdjustment}
            open={modalAdjustmentOpen}
            onCancel={() => setModalAdjustmentOpen(false)}
          />
          <StateModal
            options={getStates()}
            open={stateModalOpen}
            onCancel={onStateModalCancel}
            onCreate={onStateModalSubmit}
            loading={postLoading}
          />
       </Page>
    );
}

export default withAuthorization([RealmRoles.TransactionClaimsRead, RealmRoles.TransactionClaimsWrite,  RealmRoles.TransactionClaimsManagement ], ClaimDetail);